import { AxiosResponse } from 'axios'
import { inject, InjectionKey, ref } from 'vue'
import { User } from '../../../libs/models'
import { useApi } from '../libs/api'
import { refreshFcmTokenIfNeeded, getCurrentFcmToken } from './notification'

export interface CurrentUserStore {
  readonly value: User | null
  fetch: () => Promise<User | null>,
  create: (nickname: string) => Promise<User>,
  update: (data: { name: string, desc: string | null, illustId: number | null, coverIllustId?: number | null }) => Promise<User | null>,
  updatePassword: (password: string) => Promise<User | null>,
  enter: (roomId: number, code?: string) => Promise<User | null>,
  leave: () => Promise<User | null>,
  signin: (token: string) => Promise<User>,
  signinWithPassword: (userId: string, password: string) => Promise<User>,
  unlinkGoogle: () => Promise<User>,
  logout: () => Promise<void>,
  deleteAccount: () => Promise<void>
}
export const generateCurrentUserStore = (): CurrentUserStore => {
  const user = ref<User | null>(null)
  const api = useApi()
  const fetch = (): Promise<User | null> => {
    return api.get('/users/current').then(({ data }: AxiosResponse<User | null>) => {
      if (data) {
        user.value = data
        refreshFcmTokenIfNeeded(api)
      }
      return user.value
    })
  }
  const create = (nickname: string) => {
    return getCurrentFcmToken().then(fcmToken => {
      return api.post(`/users`, { name: nickname, fcm_token: fcmToken || undefined }).then(({ data }: AxiosResponse<User>) => {
        user.value = data
        return data
      })
    })
  }
  const update = async (data: { name: string, desc: string | null, illustId: number | null, coverIllustId?: number | null }) => {
    if (!user.value) return null
    const payload = {
      name: data.name,
      desc: data.desc,
      illust_id: data.illustId,
      cover_illust_id: data.coverIllustId
    }
    return api.post(`/users/${user.value.id}`, payload).then(({ data }: AxiosResponse<User>) => {
      user.value = data
      return data
    })
  }
  const updatePassword = async (password: string) => {
    if (!user.value) return null
    return api.post(`/users/${user.value.id}`, { password }).then(({ data }: AxiosResponse<User>) => {
      user.value = data
      return data
    })
  }
  const enter = (roomId: number, code?: string) => {
    return api.post(`/rooms/${roomId}/enter`, {}, { params: { code: code } }).then(() => {
      return fetch()
    })
  }
  const signin = (token: string) => {
    return getCurrentFcmToken().then(fcmToken => {
      return api.post('/signin', { token, fcm_token: fcmToken || undefined }).then(({ data }: AxiosResponse<User>) => {
        user.value = data
        return data
      })
    })
  }
  const signinWithPassword = (userId: number | string, password: string) => {
    return getCurrentFcmToken().then(fcmToken => {
      return api.post(`/signin/${userId}`, { password, fcm_token: fcmToken || undefined }).then(({ data }: AxiosResponse<User>) => {
        user.value = data
        return data
      })
    })
  }
  const unlinkGoogle = () => {
    return api.post('/users/current/google/unlink').then(({ data }: AxiosResponse<User>) => {
      user.value = data
      return data
    })
  }
  const leave = () => {
    return api.post('/leave').then(() => {
      return fetch()
    })
  }
  const logout = () => {
    return api.post('/signout').then(() => {
      user.value = null
    })
  }
  const deleteAccount = () => {
    return api.post('/users/current/delete').then(() => {
      user.value = null
    })
  }
  return {
    get value () {
      return user.value
    },
    fetch,
    create,
    enter,
    update,
    updatePassword,
    leave,
    signin,
    signinWithPassword,
    unlinkGoogle,
    logout,
    deleteAccount
  }
}
export const currentUserStoreKey: InjectionKey<CurrentUserStore> = Symbol()
export const useCurrentUserStore = (): CurrentUserStore => {
  const currentUserStore = inject(currentUserStoreKey)
  if (!currentUserStore) throw Error('Could not inject currentUserStore')
  return currentUserStore
}
